import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Watch } from 'lucide-react';

const LoginPage = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ email: '', password: '', dealer_name: '', company_name: '', phone: '' });
  const [mfaCode, setMfaCode] = useState('');
  const [loginStep, setLoginStep] = useState('credentials'); // 'credentials' | 'mfa'
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    setRegistrationSuccess(false);
    try {
      if (isLogin) {
        const code = loginStep === 'mfa' ? mfaCode.trim() : null;
        await login(formData.email, formData.password, code);
        navigate('/dashboard');
      } else {
        await register(formData);
        setRegistrationSuccess(true);
        setFormData({ email: '', password: '', dealer_name: '', company_name: '', phone: '' });
        navigate('/pending-approval', { replace: true });
      }
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === 'string' ? d : Array.isArray(d) ? (d[0]?.msg || d[0]) : (d?.msg || 'Authentication failed');
      const requiresMfa = msg === 'MFA code required' || err.response?.headers?.['x-requires-mfa'] === 'true';
      if (requiresMfa && loginStep === 'credentials') {
        setLoginStep('mfa');
        setError('');
      } else {
        setError(msg || 'Authentication failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafafa] flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2.5 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#0a0a0a] flex items-center justify-center">
              <Watch className="w-5 h-5 text-white" />
            </div>
            <span className="text-2xl font-semibold text-[#0a0a0a] tracking-tight">Dial-Grid</span>
          </Link>
          <p className="text-[#737373] text-sm">Watch dealer platform</p>
        </div>

        <div className="bg-white rounded-2xl border border-[#e5e5e5] p-6 md:p-8 shadow-sm">
          <div className="flex gap-1 p-1 rounded-lg bg-[#f5f5f5] mb-6">
            <button
              type="button"
              onClick={() => { setIsLogin(true); setError(''); setRegistrationSuccess(false); setLoginStep('credentials'); setMfaCode(''); }}
              className={`flex-1 py-2.5 rounded-md text-sm font-medium transition-colors ${isLogin ? 'bg-white text-[#0a0a0a] shadow-sm' : 'text-[#737373] hover:text-[#0a0a0a]'}`}
            >
              Login
            </button>
            <button
              type="button"
              onClick={() => { setIsLogin(false); setError(''); setRegistrationSuccess(false); setLoginStep('credentials'); setMfaCode(''); }}
              className={`flex-1 py-2.5 rounded-md text-sm font-medium transition-colors ${!isLogin ? 'bg-white text-[#0a0a0a] shadow-sm' : 'text-[#737373] hover:text-[#0a0a0a]'}`}
            >
              Register
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isLogin && loginStep === 'mfa' && (
              <>
                <div className="p-3 rounded-lg bg-[#f0f9ff] border border-[#bae6fd] text-sm text-[#0c4a6e] mb-2">
                  Enter the 6-digit code from your authenticator app.
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0a0a0a] mb-1.5">Authentication code</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    value={mfaCode}
                    onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="input-field text-center text-lg tracking-widest"
                    placeholder="000000"
                    maxLength={6}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => { setLoginStep('credentials'); setMfaCode(''); setError(''); }}
                  className="text-sm text-[#737373] hover:text-[#0a0a0a]"
                >
                  ← Back to password
                </button>
              </>
            )}
            {(loginStep === 'credentials' || !isLogin) && (
              <>
            <div>
              <label className="block text-sm font-medium text-[#0a0a0a] mb-1.5">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-field"
                placeholder="dealer@example.com"
                required
                readOnly={isLogin && loginStep === 'mfa'}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-[#0a0a0a]">Password</label>
                {isLogin && loginStep === 'credentials' && (
                  <Link to="/forgot-password" className="text-xs font-medium text-[#0a0a0a] hover:text-[#737373]">
                    Forgot password?
                  </Link>
                )}
              </div>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="input-field"
                placeholder="••••••••"
                required
                autoComplete={loginStep === 'mfa' ? 'current-password' : undefined}
              />
            </div>
              </>
            )}
            {!isLogin && (
              <>
                <div>
                  <label className="block text-sm font-medium text-[#0a0a0a] mb-1.5">Dealer / Business Name</label>
                  <input
                    type="text"
                    value={formData.dealer_name}
                    onChange={(e) => setFormData({ ...formData, dealer_name: e.target.value })}
                    className="input-field"
                    placeholder="Your name or business"
                    required={!isLogin}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0a0a0a] mb-1.5">Company name</label>
                  <input
                    type="text"
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="input-field"
                    placeholder="Legal entity or business name"
                    required={!isLogin}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0a0a0a] mb-1.5">Phone (optional)</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input-field"
                    placeholder="+1-555-0100"
                  />
                </div>
              </>
            )}

            {registrationSuccess && (
              <div className="p-3 rounded-lg bg-[#f0fdf4] border border-[#bbf7d0] text-sm text-[#166534]">
                <p className="font-medium mb-0.5">Account created</p>
                <p>Your account is pending approval. We&apos;ll email you once an admin approves it. You can then sign in here.</p>
              </div>
            )}
            {error && (
              <div className="p-3 rounded-lg bg-[#fef2f2] border border-[#fecaca] text-sm text-[#b91c1c]">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (isLogin && loginStep === 'mfa' && mfaCode.length !== 6)}
              className="w-full py-3 rounded-lg bg-[#0a0a0a] text-white font-medium hover:bg-[#171717] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Please wait...' : isLogin ? (loginStep === 'mfa' ? 'Verify and sign in' : 'Sign in') : 'Create account'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
